import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import { QuestionResponse } from '../../../../shared/models/projects/QuestionResponse';
import { WizardStep } from '../types/WizardStep';
import { ProjectData } from '../models/ProjectData';
import { SearchVolumeResponse } from '../../../../shared/models/projects/SearchVolumeResponse';
import { MonitoringSchedulePreset, presetOfInterval } from '../../../../shared/models/projects/MonitoringInterval';

interface ProjectWizardContextType {
  domain: string | null;
  setDomain: Dispatch<SetStateAction<string | null>>;
  firmName: string | null;
  setFirmName: Dispatch<SetStateAction<string | null>>;
  activeStep: WizardStep;
  setActiveStep: Dispatch<SetStateAction<WizardStep>>;
  questionResponse: QuestionResponse | null;
  setQuestionResponse: Dispatch<SetStateAction<QuestionResponse | null>>;
  projectData: ProjectData | null;
  setProjectData: Dispatch<SetStateAction<ProjectData | null>>;
  searchVolume: SearchVolumeResponse[] | null;
  setSearchVolume: Dispatch<SetStateAction<SearchVolumeResponse[] | null>>;
  /** Chosen in the last step (addon-gated); sent with the create only when the account may set it. */
  monitoringPreset: MonitoringSchedulePreset;
  setMonitoringPreset: Dispatch<SetStateAction<MonitoringSchedulePreset>>;
}

export const ProjectWizardContext = createContext<ProjectWizardContextType | undefined>(undefined);

export const ProjectWizardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [domain, setDomain] = useState<string | null>(null);
  const [firmName, setFirmName] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<WizardStep>('DOMAIN');
  const [questionResponse, setQuestionResponse] = useState<QuestionResponse | null>(null);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [searchVolume, setSearchVolume] = useState<SearchVolumeResponse[] | null>(null);
  // Starts from the account's frequency (set by an admin); the user with the addon may change it.
  const { accountResponse } = useAccount();
  const accountPreset = presetOfInterval(accountResponse?.monitoringInterval);
  const [monitoringPreset, setMonitoringPreset] = useState<MonitoringSchedulePreset>(accountPreset);
  useEffect(() => {
    setMonitoringPreset(accountPreset);
  }, [accountPreset]);

  return (
    <ProjectWizardContext.Provider
      value={{
        domain,
        setDomain,
        firmName,
        setFirmName,
        activeStep,
        setActiveStep,
        questionResponse,
        setQuestionResponse,
        projectData,
        setProjectData,
        searchVolume,
        setSearchVolume,
        monitoringPreset,
        setMonitoringPreset,
      }}
    >
      {children}
    </ProjectWizardContext.Provider>
  );
};

export const useProjectWizard = () => {
  const context = useContext(ProjectWizardContext);
  if (!context) {
    throw Error('useProjectWizard must be used with an ProjectWizardProvider');
  }

  return context;
};
